import React, { useState } from "react";
import { toast } from "sonner";
import { KeyRound, Eye, EyeOff, ShieldCheck } from "lucide-react";
import api from "@/lib/api";

const MIN_LEN = 12;

export default function ChangePasswordSection() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const tooShort = next.length > 0 && next.length < MIN_LEN;
  const mismatch = confirm.length > 0 && next !== confirm;
  const same = next.length > 0 && next === current;

  const canSubmit = current && next.length >= MIN_LEN && next === confirm && !same && !submitting;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password updated");
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to change password");
    } finally {
      setSubmitting(false);
    }
  };

  const Field = ({ value, setter, placeholder, testid, autoComplete }) => (
    <input
      type={show ? "text" : "password"}
      value={value}
      onChange={(e) => setter(e.target.value)}
      placeholder={placeholder}
      autoComplete={autoComplete}
      data-testid={testid}
      className="w-full px-3 py-2.5 rounded outline-none text-sm font-mono-vault"
      style={{ background: "var(--vault-bg)", border: "1px solid var(--vault-border)" }}
    />
  );

  return (
    <form
      onSubmit={submit}
      className="rounded-lg p-8 border space-y-5"
      style={{ background: "var(--vault-surface)", borderColor: "var(--vault-border)" }}
      data-testid="change-password-section"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <KeyRound className="w-5 h-5" style={{ color: "var(--vault-gold)" }} />
          <h2 className="font-display text-2xl">Security · Change password</h2>
        </div>
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          className="text-xs inline-flex items-center gap-1.5 text-[var(--vault-text-muted)] hover:text-white"
          title={show ? "Hide passwords" : "Show passwords"}
        >
          {show ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          {show ? "Hide" : "Show"}
        </button>
      </div>

      <div>
        <label className="overline block text-[var(--vault-text-muted)] mb-2">Current password</label>
        <Field value={current} setter={setCurrent} placeholder="Enter your current password" testid="cp-current" autoComplete="current-password" />
      </div>

      <div>
        <label className="overline block text-[var(--vault-text-muted)] mb-2">New password</label>
        <Field value={next} setter={setNext} placeholder={`Minimum ${MIN_LEN} characters`} testid="cp-new" autoComplete="new-password" />
        {tooShort && <p className="text-[10px] mt-1" style={{ color: "#FCA5A5" }}>Password must be at least {MIN_LEN} characters.</p>}
        {same && <p className="text-[10px] mt-1" style={{ color: "#FCA5A5" }}>New password must differ from the current one.</p>}
      </div>

      <div>
        <label className="overline block text-[var(--vault-text-muted)] mb-2">Confirm new password</label>
        <Field value={confirm} setter={setConfirm} placeholder="Repeat the new password" testid="cp-confirm" autoComplete="new-password" />
        {mismatch && <p className="text-[10px] mt-1" style={{ color: "#FCA5A5" }}>Passwords do not match.</p>}
      </div>

      <div className="flex items-center justify-between pt-4 border-t" style={{ borderColor: "var(--vault-border)" }}>
        <p className="text-[10px] text-[var(--vault-text-faint)] max-w-md">
          Existing sessions stay valid until they expire. Sign out and back in to make sure your devices use the new password.
        </p>
        <button
          type="submit"
          disabled={!canSubmit}
          data-testid="cp-submit"
          className="btn-gold inline-flex items-center gap-2 px-6 py-2.5 rounded-md text-sm"
        >
          {submitting ? <span className="spin" /> : <ShieldCheck className="w-4 h-4" />}
          Update password
        </button>
      </div>
    </form>
  );
}
